/**
 * BrandHealth.jsx
 * Whether each brand can actually publish.
 *
 * This is the state that quietly stops everything: a token expires, nothing
 * says so, and the first anyone knows is a post that did not go out. So it is
 * on the first screen, per brand, with the offending platform named rather
 * than a single overall light.
 *
 * Dependencies: helpers.js, components/Icons.jsx
 */

import { Link, Plus, Pulse, Warning } from '../Icons.jsx';
import { PLATFORMS } from './helpers.js';

/** The worst state any of a brand's accounts is in. */
function summarise(accounts = []) {
  if (!accounts.length) return { tone: 'warn', label: 'Not connected' };
  if (accounts.some((a) => a.status === 'error' || a.status === 'disconnected')) {
    return { tone: 'danger', label: 'Needs reconnecting' };
  }
  if (accounts.some((a) => a.status === 'expiring')) return { tone: 'warn', label: 'Expiring soon' };
  return { tone: 'ok', label: 'Healthy' };
}

const MARK_STATE = {
  connected: 'is-ok',
  expiring: 'is-warn',
};

export default function BrandHealth({ brands = [], navigate }) {
  const unhealthy = brands.filter((b) => summarise(b.accounts).tone !== 'ok').length;

  return (
    <section className="card">
      <div className="card__head">
        <span className={`tile ${unhealthy ? 'tile--warn' : 'tile--ok'}`}>
          <Pulse size={21} />
        </span>
        <div className="card__head-text">
          <h2>Brand health</h2>
          <p>Whether each brand can publish right now.</p>
        </div>
        <div className="card__head-end">
          <button className="btn btn--sm" onClick={() => navigate('/brands')}>
            View all
          </button>
        </div>
      </div>

      {brands.length === 0 ? (
        <div className="empty empty--inline">
          <div className="empty__art">
            <Link size={26} />
          </div>
          <h3>No brands yet</h3>
          <p>Add a brand and connect its Facebook Page to start publishing.</p>
          <button className="btn btn--primary" onClick={() => navigate('/brands')}>
            <Plus /> Add a brand
          </button>
        </div>
      ) : (
        <div className="health-list">
          {brands.map((brand) => {
            const state = summarise(brand.accounts);
            return (
              <button
                className="health-row"
                key={brand.id}
                type="button"
                onClick={() => navigate('/brands')}
              >
                <span className={`health-row__mark health-row__mark--${state.tone}`}>
                  {brand.name.slice(0, 1).toUpperCase()}
                </span>

                <span className="health-row__text">
                  <span className="row__label">{brand.name}</span>
                  <span className="platform-marks">
                    {(brand.accounts ?? []).map((account) => {
                      const meta = PLATFORMS[account.platform];
                      if (!meta) return null;
                      const { Icon: Mark, label } = meta;
                      return (
                        <span
                          className={`platform-mark ${MARK_STATE[account.status] ?? 'is-off'}`}
                          key={account.id ?? account.platform}
                          title={`${label} — ${account.status}`}
                        >
                          <Mark size={13} />
                          <span className="sr-only">
                            {label}: {account.status}
                          </span>
                        </span>
                      );
                    })}
                    {(brand.accounts ?? []).length === 0 && (
                      <span className="row__sub">No accounts connected</span>
                    )}
                  </span>
                </span>

                <span className={`badge badge--${state.tone}`}>
                  {state.tone === 'ok' ? <span className="dot" /> : <Warning size={13} />}
                  {state.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
